// Convention d'affichage des valeurs absentes :
// - "-" pour cellules de tableau et inline (formatPrice, formatTonnage, formatDate retournent "-").
// - "Non renseigne" pour fiches detaillees et mode lecture.
// (Les libelles "Non assigne/affecte/rattache" sont distincts : etat metier, pas absence.)

import { clsx, type ClassValue } from "clsx"
import { extendTailwindMerge } from "tailwind-merge"
import { LOCALE_FR } from "./labels"

// Etend tailwind-merge avec nos tokens typo custom (definis dans index.css @theme inline).
// Sans cette extension, twMerge ne reconnait pas text-overline / text-caption / etc.
// comme appartenant au groupe font-size et peut les supprimer silencieusement quand
// une classe text-{color} est presente dans le meme appel a cn() (cas observe sur
// AccordionTrigger : text-overline avale a cause de text-navy-dark/60).
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [
        'text-display',
        'text-heading',
        'text-subheading',
        'text-body',
        'text-caption',
        'text-overline',
      ],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Encode un Blob/File en base64 (chunked pour eviter le stack overflow sur gros fichiers)
export async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

// Decode une chaine base64 en Uint8Array
export function base64ToBytes(b64: string): Uint8Array {
  const binaryStr = atob(b64);
  const bytes = new Uint8Array(binaryStr.length);
  for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i);
  return bytes;
}

// Formate un nom complet a partir d'un prenom et nom optionnels.
// Retourne '' si les deux sont vides, sinon joint avec un espace.
export function formatFullName(
  prenom: string | null | undefined,
  nom: string | null | undefined
): string {
  return [prenom, nom].filter(Boolean).join(' ');
}

// Formate un bloc contact pour les PDF : "Nom (Telephone)" ou "Nom" si pas de telephone.
// Retourne '' si le nom est vide. Utilise dans Fiche CAP, confirmation d'affretement,
// Fiche transport.
export function formatContactInfo(
  nom: string | null | undefined,
  telephone: string | null | undefined
): string {
  if (!nom) return '';
  return telephone ? `${nom} (${telephone})` : nom;
}

// Montant a 2 decimales, sans symbole (cartes de totaux qui affichent « € » a part).
export function formatMontant(montant: number): string {
  return montant.toLocaleString(LOCALE_FR, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Formate un montant en euros au format francais. Retourne '-' si null/undefined.
// Format : "1 234,56 €" (separateur FR, symbole apres, espace insecable).
// Utilise pour affichage UI (listes devis/factures, totaux).
// Pas utilise pour PDF (Carbone gere son propre formatage).
export function formatPrice(montant: number | null | undefined): string {
  if (montant == null) return '-';
  return `${formatMontant(montant)} €`;
}

// Formate un tonnage en tonnes au format francais. Retourne '-' si null/undefined.
// Format : "12,50 t" (separateur FR, minuscule, 2 decimales, espace insecable
// avant l'unite). Utilise pour affichage UI (cartes BSD, tableaux CAP, fiches).
// Pas utilise pour PDF (Carbone gere son propre formatage).
export function formatTonnage(tonnage: number | null | undefined): string {
  if (tonnage == null) return '-';
  return tonnage.toLocaleString(LOCALE_FR, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + ' t';
}

// Formate une surface en metres carres au format francais (RC2.86, D-CAP-M2).
// Symetrique de formatTonnage. Unite en ASCII `m2` (D-UX-M3-ASCII : pas d'exposant
// Unicode, qui casse les recherches et l'alignement avec la donnee).
export function formatSurface(m2: number | null | undefined): string {
  if (m2 == null) return '-';
  return m2.toLocaleString(LOCALE_FR, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + ' m2';
}

// Enumere les quantites renseignees d'un dechet : "1,20 t", "2,10 m2", ou "1,20 t · 2,10 m2".
// RC2.86 : reserve aux affichages SANS libelle, ou l'unite portee par la valeur suffit a
// identifier la grandeur (cellule de liste, colonne). Partout ou un libelle existe, afficher
// « Tonnage : » et « Surface : » separement — ce sont deux informations complementaires.
export function formatQuantitesDechet(
  tonnage: number | null | undefined,
  m2: number | null | undefined,
): string {
  const parts: string[] = [];
  if (tonnage != null && tonnage > 0) parts.push(formatTonnage(tonnage));
  if (m2 != null && m2 > 0) parts.push(formatSurface(m2));
  return parts.length > 0 ? parts.join(' · ') : '-';
}

// Formate une Date en `YYYY-MM-DD` base sur la date LOCALE (pas UTC).
// A utiliser pour persister une date metier en BDD (`dateDebut` chantier,
// `date` devis, `date` facture). NE PAS utiliser `.toISOString().slice(0,10)`
// qui peut renvoyer la veille en fin de journee selon la TZ du navigateur.
// Cf. D-UX-DATE-BACKEND-HELPER (PLAN/decisions_developpement.md).
export function formatDateForBackend(d: Date): string {
  // Composants LOCAUX natifs (ex-date-fns format 'yyyy-MM-dd', lot 6 boilerplate) :
  // getFullYear/getMonth/getDate lisent la TZ locale, comme date-fns le faisait.
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// Code postal francais : 5 chiffres. Source unique pour les validations de saisie manuelle
// (SermeInfosForm, modales d'edition etablissement/ET). Les flux Google Places n'en ont pas
// besoin (CP fourni par l'API).
export const CODE_POSTAL_REGEX = /^\d{5}$/;

// Recherche par numero dans les listes a cocher (fenetres de creation, ajout de sources) :
// un des numeros contient le terme, sans tenir compte de la casse ; terme vide = tout.
export function correspondNumero(terme: string, ...numeros: (string | null | undefined)[]): boolean {
  const t = terme.trim().toLowerCase();
  return !t || numeros.some((n) => !!n && n.toLowerCase().includes(t));
}

// Format email, meme motif que les CHECK en base (chk_transporteurs_email_ops,
// chk_etablissements_emails_facturation) : message clair avant le refus de la base.
export const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// Saisie de plusieurs adresses (virgule, point-virgule ou espace) : liste sans doublon, casse ignoree,
// dans l'ordre saisi (la premiere ecriture est gardee).
export function splitEmails(saisie: string): string[] {
  const vues = new Set<string>();
  return saisie.split(/[\s,;]+/).filter((email) => {
    const cle = email.toLowerCase();
    if (!email || vues.has(cle)) return false;
    vues.add(cle);
    return true;
  });
}

// Reciproque de parseAddressInput : concatene rue/CP/ville en "rue, CP ville" pour les
// templates Carbone (CAP, FID, confirmations affretement).
export function formatAdresse(parts: { rue: string; codePostal: string; ville: string }): string {
  return `${parts.rue}, ${parts.codePostal} ${parts.ville}`;
}

// Parse une saisie d'adresse libre vers les 3 composants rue/CP/ville. Strategie : reperer le
// DERNIER nombre a 5 chiffres (le CP ; une boite postale « CS 70021 » / « BP 12345 » le precede)
// et splitter avant/apres, sinon split par derniere virgule.
export function parseAddressInput(input: string): { rue: string; codePostal: string; ville: string } {
  const trimmed = input.trim();
  const cpMatch = [...trimmed.matchAll(/\b(\d{5})\b/g)].at(-1);
  if (cpMatch && cpMatch.index !== undefined) {
    const cp = cpMatch[1];
    const before = trimmed.slice(0, cpMatch.index).trim().replace(/[,;]\s*$/, '');
    const after = trimmed.slice(cpMatch.index + cp.length).trim().replace(/^[,;]\s*/, '');
    return { rue: before, codePostal: cp, ville: after };
  }
  const lastComma = trimmed.lastIndexOf(',');
  if (lastComma > 0) {
    return { rue: trimmed.slice(0, lastComma).trim(), codePostal: '', ville: trimmed.slice(lastComma + 1).trim() };
  }
  return { rue: trimmed, codePostal: '', ville: '' };
}

// Re-exports de compatibilite — contenu deplace lot 4 boilerplate (decontamination
// du socle). Importer depuis les fichiers modules pour le nouveau code.
export { formatProducteurNom } from '@/services/chantiersLabels';
export { buildPdfSubjectFromChantierOrTransport } from '@/services/devisFactureLabels';
